import pick from "lodash/pick";
import {
  FIELD_CONVERTED_AVAILABILITY_KEY,
  FIELD_CUSTOM_FIELDS_KEY,
  FIELD_METADATA_KEY
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { sanitizeFormDataForAPI } from "PFApp/helpers/activity_form/data_conversions";
import {
  PendingEdit,
  PendingEdits,
  PendingEditStatus
} from "PFApp/stores/manage_roles/manage_roles_store.types";
import { ManageRolesFormType } from "PFApp/types/activity_form/manage_roles_form_schema";
import { AvailabilityMode, Id } from "PFCore/types";

type RoleEditGroup = { keys: string[]; edits: PendingEdit[] };

type SanitizedPayload = ReturnType<typeof sanitizeFormDataForAPI>;

type CustomFieldEntry = { type: { name: string }; values: unknown[] };

const getSubFieldName = (fieldPath: string, parent: string): string | null => {
  const prefix = `${parent}.`;

  if (fieldPath.startsWith(prefix)) {
    return fieldPath.slice(prefix.length);
  }

  return null;
};

const buildFilteredPayload = (
  full: SanitizedPayload,
  fieldNames: string[],
  rawValues: ManageRolesFormType
): Record<string, unknown> => {
  const result: Record<string, unknown> = { id: full.id };

  const metadataSubKeys: string[] = [];
  const customFieldSubKeys = new Set<string>();
  const topLevelKeys: string[] = [];

  for (const fieldName of fieldNames) {
    if (fieldName === FIELD_CONVERTED_AVAILABILITY_KEY) {
      const mode = rawValues.metadata?.availability?.mode;
      const { convertedAvailability } = rawValues;

      const existingMetadata = result.metadata ?? {};

      if (mode === AvailabilityMode.PerWeek) {
        result.metadata = {
          ...existingMetadata,
          availability: {
            ...(full?.metadata?.availability ?? {}),
            ranges: convertedAvailability.ranges
          }
        };
      }
      continue;
    }

    const metadataKey = getSubFieldName(fieldName, FIELD_METADATA_KEY);
    const customFieldKey = getSubFieldName(fieldName, FIELD_CUSTOM_FIELDS_KEY);

    if (metadataKey) {
      metadataSubKeys.push(metadataKey);
    } else if (customFieldKey) {
      customFieldSubKeys.add(customFieldKey);
    } else if (fieldName !== "id") {
      topLevelKeys.push(fieldName);
    }
  }

  if (metadataSubKeys.length > 0) {
    result.metadata = pick(full.metadata as Record<string, unknown>, metadataSubKeys);
  }

  if (customFieldSubKeys.size > 0) {
    result.customFields = (full.custom_fields as CustomFieldEntry[]).filter((entry) =>
      customFieldSubKeys.has(entry.type.name)
    );
  }

  if (topLevelKeys.length > 0) {
    Object.assign(result, pick(full as Record<string, unknown>, topLevelKeys));
  }

  return result;
};

export const groupEditsByRole = (edits: Record<string, PendingEdit>): Map<Id, RoleEditGroup> =>
  Object.entries(edits).reduce((byRole, [key, edit]) => {
    const group = byRole.get(edit.roleId) ?? { keys: [], edits: [] };

    group.keys.push(key);
    group.edits.push(edit);

    return byRole.set(edit.roleId, group);
  }, new Map<Id, RoleEditGroup>());

export const buildRolePayload = (roleValues: ManageRolesFormType, edits: PendingEdit[]) => {
  const full = sanitizeFormDataForAPI({ values: roleValues, isNew: false });
  const changedFieldNames = edits.map((edit) => edit.fieldName);

  return buildFilteredPayload(full, changedFieldNames, roleValues);
};

export const getPendingEditsWithStatus = (pendingEdits: PendingEdits, status: PendingEditStatus) =>
  Object.fromEntries(Object.entries(pendingEdits).filter(([, edit]) => edit.status === status));
